import { useMemo, useState } from 'react'
import { Link, useNavigate, useSearchParams } from 'react-router-dom'

import { api } from '../api/client'
import type { Supplier, SupplierCandidate, SupplierOverview } from '../api/types'
import { usePermessiModuli } from '../auth/permessi'
import { BottoniEsporta } from '../components/BottoniEsporta'
import { CriticitaBadge } from '../components/BusinessApps'
import { Card, EmptyState, ErrorBanner, Spinner, StatCard } from '../components/common'
import { DataTable, type Column } from '../components/DataTable'
import { FinestraFornitore, etichettaSostituibilita, etichettaTipoFornitore } from '../components/Fornitori'
import { TabellaRichieste } from '../components/RichiesteFornitore'
import { useAsync } from '../hooks/useAsync'
import { localeCorrente, t } from '../i18n/lingua'

/**
 * Suppliers & Contracts → Fornitori: the registry of every third party.
 *
 * CENSO reads the names in its data — who publishes the installed software
 * (operating system packages go to the distribution), who makes the hardware
 * it manages, the cloud providers, the vendors written in contracts, assets
 * and CRA products — and proposes them in «Da catalogare»: a person accepts,
 * merges into a supplier or ignores. Each supplier then has its footprint:
 * programs, CIs, open findings (exploited first), end of support, contracts,
 * and the criticality of what it directly supports. One route per view:
 * /suppliers (overview), /suppliers/list, /suppliers/catalog, /suppliers/ict
 * (the ICT providers, also in Compliance). See supplier_service.
 */

export type VistaFornitori = 'panoramica' | 'elenco' | 'catalogo' | 'ict' | 'richieste'

const PERCORSI: Record<VistaFornitori, string> = {
  panoramica: '/suppliers', elenco: '/suppliers/list', catalogo: '/suppliers/catalog', ict: '/suppliers/ict',
  richieste: '/suppliers/requests',
}

const n = (x: number | undefined) => (x ?? 0).toLocaleString(localeCorrente())

function etichettaFonte(f: string): string {
  switch (f) {
    case 'software': return t('software installato')
    case 'os': return t('sistema operativo')
    case 'hardware': return t('hardware')
    case 'cloud': return t('cloud')
    case 'contract': return t('contratti')
    case 'asset': return t('asset')
    case 'cra': return t('CRA')
    default: return f
  }
}

export default function Fornitori({ vista }: { vista: VistaFornitori }) {
  const navigate = useNavigate()
  const permessi = usePermessiModuli()
  const scrive = permessi.scrive('suppliers')
  const panoramica = useAsync(() => api.supplierOverview(), [])
  const [aggiorno, setAggiorno] = useState(false)
  const [errore, setErrore] = useState<unknown>(null)
  const [nuovo, setNuovo] = useState(false)
  // A new catalogue run remounts the lists, so they read again.
  const [giro, setGiro] = useState(0)

  async function aggiorna() {
    setAggiorno(true)
    setErrore(null)
    try {
      await api.refreshSuppliers()
      await panoramica.reload()
      setGiro((g) => g + 1)
    } catch (err) {
      setErrore(err)
    } finally {
      setAggiorno(false)
    }
  }

  const p = panoramica.data
  return (
    <>
      <header className="page__header">
        <div>
          <h1 className="page__title">{vista === 'ict' ? t('Fornitori ICT') : t('Fornitori')}</h1>
          <p className="page__subtitle">
            {vista === 'ict'
              ? t('i fornitori di servizi ICT e cloud del registro, con i contratti, le funzioni che sostengono e la loro criticità: la base del registro delle informazioni DORA e della sicurezza della catena di fornitura NIS2.')
              : t('il registro di tutte le terze parti: CENSO propone i nomi che trova (chi pubblica il software installato, chi produce l’hardware, i cloud, i fornitori scritti nei contratti e negli asset) e tu li confermi; per ogni fornitore, che cosa gira di suo, dove, con quali vulnerabilità e scadenze, e per quali funzioni dell’organizzazione.')}
          </p>
        </div>
        <div className="toolbar">
          {scrive && <button type="button" className="secondary" onClick={() => void aggiorna()} disabled={aggiorno}>
            {aggiorno ? t('Aggiornamento…') : t('Aggiorna')}
          </button>}
          {scrive && <button type="button" onClick={() => setNuovo(true)}>{t('Nuovo fornitore')}</button>}
          <BottoniEsporta nome="censo-fornitori" path={api.suppliersExportPath} />
        </div>
      </header>
      <nav className="tabs" role="tablist">
        {([['panoramica', t('Panoramica')], ['elenco', t('Fornitori')], ['catalogo', t('Da catalogare ({n})', { n: p?.candidates ?? 0 })],
          ['richieste', t('Richieste')]] as [VistaFornitori, string][]).map(([v, l]) => (
          <button key={v} type="button" role="tab" aria-selected={vista === v}
                  className={`tabs__tab${vista === v ? ' tabs__tab--active' : ''}`} onClick={() => navigate(PERCORSI[v])}>{l}</button>
        ))}
      </nav>
      <ErrorBanner error={errore ?? panoramica.error} />
      {p?.last_run_at && (
        <p className="field__hint">{t('Ultimo catalogo: {quando}. CENSO lo rifà da solo ogni ora.', {
          quando: new Date(p.last_run_at).toLocaleString(localeCorrente()) })}</p>
      )}
      {vista === 'panoramica' && (p ? <Panoramica p={p} vai={(v, filtro) => navigate(`${PERCORSI[v]}${filtro ? `?filtro=${filtro}` : ''}`)} /> : <Spinner />)}
      {vista === 'elenco' && <Elenco key={giro} />}
      {vista === 'ict' && <Elenco key={giro} soloIct />}
      {vista === 'catalogo' && <Catalogo key={giro} scrive={scrive} onCambiato={() => void panoramica.reload()} />}
      {vista === 'richieste' && <TutteLeRichieste scrive={scrive} />}
      {nuovo && (
        <FinestraFornitore onAnnulla={() => setNuovo(false)}
                           onFatto={(s) => { setNuovo(false); navigate(`/suppliers/${s.id}`) }} />
      )}
    </>
  )
}

function Panoramica({ p, vai }: { p: SupplierOverview; vai: (v: VistaFornitori, filtro?: string) => void }) {
    if (p.suppliers === 0 && p.candidates === 0) {
      return (
        <Card>
          <EmptyState title={t('Il registro è vuoto')}
                      hint={t('CENSO propone i fornitori leggendo inventario, contratti e asset: premi «Aggiorna» per il primo catalogo, poi conferma le proposte in «Da catalogare».')} />
        </Card>
      )
    }
    return (
      <div className="stats">
        <StatCard label={t('Fornitori')} value={n(p.suppliers)} onClick={() => vai('elenco')} />
        <StatCard label={t('Da catalogare')} value={n(p.candidates)} tone={p.candidates ? 'warn' : 'ok'}
                  hint={t('nomi trovati e non ancora confermati')} onClick={() => vai('catalogo')} />
        <StatCard label={t('Critici o importanti')} value={n(p.critical)}
                  hint={t('sostengono funzioni critiche o alte')} onClick={() => vai('elenco', 'critici')} />
        <StatCard label={t('Con vulnerabilità sfruttate')} value={n(p.with_exploited)} tone={p.with_exploited ? 'error' : 'ok'}
                  hint={t('nei loro prodotti installati qui')} onClick={() => vai('elenco', 'sfruttate')} />
        <StatCard label={t('Con prodotti fuori supporto')} value={n(p.with_end_of_support)}
                  tone={p.with_end_of_support ? 'warn' : 'ok'} onClick={() => vai('elenco', 'fuori_supporto')} />
        <StatCard label={t('Senza contatto di sicurezza')} value={n(p.without_security_contact)}
                  tone={p.without_security_contact ? 'warn' : 'ok'}
                  hint={t('a chi si segnala una vulnerabilità?')} onClick={() => vai('elenco', 'senza_contatto')} />
        <StatCard label={t('Fornitori ICT e cloud')} value={n(p.ict)} onClick={() => vai('ict')} />
      </div>
    )
}

type Filtro = 'tutti' | 'critici' | 'sfruttate' | 'fuori_supporto' | 'senza_contatto' | 'cessati'

function nelFiltro(s: Supplier, f: Filtro): boolean {
  switch (f) {
    case 'critici': return s.criticality === 'critical' || s.criticality === 'high'
    case 'sfruttate': return (s.footprint.exploited ?? 0) > 0
    case 'fuori_supporto': return (s.footprint.end_of_support ?? 0) > 0
    case 'senza_contatto': return !s.security_contact && s.status === 'active'
    case 'cessati': return s.status === 'ceased'
    default: return s.status === 'active'
  }
}

function Elenco({ soloIct = false }: { soloIct?: boolean }) {
  const [params, setParams] = useSearchParams()
  const filtro = (params.get('filtro') ?? 'tutti') as Filtro
  const dati = useAsync(() => api.suppliers(), [])
  const righe = useMemo(() => (dati.data ?? [])
    .filter((s) => !soloIct || s.is_ict)
    .filter((s) => nelFiltro(s, filtro)), [dati.data, filtro, soloIct])
  const colonne: Column<Supplier>[] = [
    { key: 'name', header: t('Fornitore'), render: (s) => (
      <>
        <Link to={`/suppliers/${s.id}`}><strong>{s.name}</strong></Link>
        {s.system && <> <span className="badge badge--info">{t('gestito da CENSO')}</span></>}
        {s.footprint.examples && s.footprint.examples.length > 0 && (
          <div className="text-muted eagle-piccolo">{s.footprint.examples.slice(0, 3).join(', ')}</div>
        )}
      </>
    ), value: (s) => `${s.name} ${(s.footprint.examples ?? []).join(' ')}` },
    { key: 'kinds', header: t('Tipo'), value: (s) => s.kinds.map(etichettaTipoFornitore).join(', ') },
    { key: 'criticality', header: t('Criticità'), value: (s) => s.criticality ?? '', render: (s) => <CriticitaBadge value={s.criticality} /> },
    { key: 'cis', header: t('CI'), value: (s) => s.footprint.cis ?? 0 },
    { key: 'installations', header: t('Installazioni'), value: (s) => s.footprint.installations ?? 0 },
    { key: 'findings', header: t('Reperti aperti'), value: (s) => s.footprint.findings ?? 0 },
    { key: 'exploited', header: t('Sfruttate'), value: (s) => s.footprint.exploited ?? 0,
      render: (s) => (s.footprint.exploited ? <span className="badge badge--error">{n(s.footprint.exploited)}</span> : '0') },
    { key: 'end_of_support', header: t('Fuori supporto'), value: (s) => s.footprint.end_of_support ?? 0 },
    { key: 'active_contracts', header: t('Contratti in vigore'), value: (s) => s.footprint.active_contracts ?? 0 },
    { key: 'security_contact', header: t('Contatto di sicurezza'), value: (s) => s.security_contact,
      render: (s) => s.security_contact || <span className="text-muted">—</span> },
    ...(soloIct ? [
      { key: 'lei', header: t('LEI'), mono: true, value: (s: Supplier) => s.lei,
        render: (s: Supplier) => s.lei || <span className="badge badge--warn">{t('manca')}</span> },
      { key: 'substitutability', header: t('Sostituibilità'), value: (s: Supplier) => s.substitutability ?? '',
        render: (s: Supplier) => (s.substitutability ? etichettaSostituibilita(s.substitutability) : <span className="text-muted">{t('non valutata')}</span>) },
      { key: 'exit', header: t('Strategia di uscita'), value: (s: Supplier) => (s.exit_strategy ? (s.exit_tested ? 2 : 1) : 0),
        render: (s: Supplier) => (s.exit_strategy ? (s.exit_tested ? t('scritta e provata') : t('scritta')) : <span className="text-muted">—</span>) },
      { key: 'data_locations', header: t('Sedi dei dati'), value: (s: Supplier) => s.data_locations.join(', ') },
    ] as Column<Supplier>[] : []),
  ]
  const segmenti: [Filtro, string][] = [
    ['tutti', t('In uso')], ['critici', t('Critici')], ['sfruttate', t('Con sfruttate')],
    ['fuori_supporto', t('Fuori supporto')], ['senza_contatto', t('Senza contatto di sicurezza')], ['cessati', t('Cessati')],
  ]
  return (
    <Card>
      <div className="segmented" role="tablist">
        {segmenti.map(([k, l]) => (
          <button key={k} type="button" className={filtro === k ? 'segmented__on' : ''} aria-selected={filtro === k}
                  onClick={() => setParams(k === 'tutti' ? {} : { filtro: k })}>{l}</button>
        ))}
      </div>
      <ErrorBanner error={dati.error} />
      {soloIct && (
        <div className="toolbar" style={{ justifyContent: 'space-between' }}>
          <span className="field__hint">{t('La base del registro delle informazioni DORA: fornitori ICT, accordi contrattuali, funzioni sostenute. Una base per i modelli EBA, non la tassonomia completa.')}</span>
          <BottoniEsporta nome="censo-registro-dora" path={api.doraRegisterExportPath} />
        </div>
      )}
      {dati.loading && !dati.data ? <Spinner /> : righe.length === 0 ? (
        <EmptyState title={t('Nessun fornitore qui')}
                    hint={soloIct ? t('Un fornitore è ICT quando nella sua scheda ha il tipo «Servizi ICT» o «Cloud».')
                      : t('Le proposte da confermare sono in «Da catalogare».')} />
      ) : (
        <DataTable rows={righe} columns={colonne} rowKey={(s) => s.id} conteggio={{ nome: t('fornitori') }}
                   initialSort={{ key: 'exploited', dir: 'desc' }} />
      )}
    </Card>
  )
}

function Catalogo({ scrive, onCambiato }: { scrive: boolean; onCambiato: () => void }) {
  const [stato, setStato] = useState<'proposed' | 'ignored'>('proposed')
  const dati = useAsync(() => api.supplierCandidates(stato), [stato])
  const fornitori = useAsync(() => api.supplierOptions(), [])
  const [scelti, setScelti] = useState<Set<string>>(new Set())
  const [unisciIn, setUnisciIn] = useState('')
  const [busy, setBusy] = useState(false)
  const [errore, setErrore] = useState<unknown>(null)
  const [esito, setEsito] = useState('')

  async function esegui(fn: () => Promise<string>) {
    setBusy(true)
    setErrore(null)
    try {
      setEsito(await fn())
      setScelti(new Set())
      await Promise.all([dati.reload(), fornitori.reload()])
      onCambiato()
    } catch (err) {
      setErrore(err)
    } finally {
      setBusy(false)
    }
  }
  const chiavi = [...scelti]
  const colonne: Column<SupplierCandidate>[] = [
    { key: 'label', header: t('Nome'), render: (c) => (
      <>
        <strong>{c.label}</strong>
        {c.labels.length > 1 && <div className="text-muted eagle-piccolo">{c.labels.slice(0, 4).join(' · ')}</div>}
      </>
    ), value: (c) => `${c.label} ${c.labels.join(' ')}` },
    { key: 'sources', header: t('Dove l’ha visto'), value: (c) => Object.keys(c.sources).map(etichettaFonte).join(', '),
      render: (c) => Object.entries(c.sources).map(([f, k]) => `${etichettaFonte(f)} ${n(k)}`).join(' · ') },
    { key: 'seen', header: t('Volte'), value: (c) => c.seen },
    { key: 'examples', header: t('Esempi'), value: (c) => c.examples.join(', '),
      render: (c) => <span className="text-muted">{c.examples.slice(0, 4).join(', ')}</span> },
    { key: 'suggested', header: t('Somiglia a'), value: (c) => c.suggested_supplier_name ?? '',
      render: (c) => c.suggested_supplier_id
        ? <Link to={`/suppliers/${c.suggested_supplier_id}`}>{c.suggested_supplier_name}</Link> : '' },
  ]
  return (
    <Card>
      <p className="field__hint" style={{ marginTop: 0 }}>
        {t('I nomi che CENSO ha trovato e che non sono ancora un fornitore, con dove li ha visti. Nomi scritti in modi diversi («Microsoft Corporation», «Microsoft Corp.») sono già uniti; i pacchetti del sistema operativo sono della distribuzione, non di chi li ha impacchettati. Accetta per creare un fornitore per ciascuno, unisci a un fornitore esistente, o ignora: un nome ignorato non torna.')}
      </p>
      <div className="segmented" role="tablist">
        <button type="button" className={stato === 'proposed' ? 'segmented__on' : ''} onClick={() => { setStato('proposed'); setScelti(new Set()) }}>{t('Proposti')}</button>
        <button type="button" className={stato === 'ignored' ? 'segmented__on' : ''} onClick={() => { setStato('ignored'); setScelti(new Set()) }}>{t('Ignorati')}</button>
      </div>
      <ErrorBanner error={errore ?? dati.error} />
      {esito && <p className="field__hint" role="status">✓ {esito}</p>}
      {scrive && scelti.size > 0 && (
        <div className="selezione-barra" role="region" aria-label={t('Righe selezionate')}>
          <span>{t('{n} selezionate', { n: scelti.size })}</span>
          {stato === 'proposed' ? (
            <>
              <button type="button" disabled={busy} onClick={() => void esegui(async () => {
                const r = await api.acceptSupplierCandidates(chiavi)
                return t('{n} fornitori creati', { n: r.created })
              })}>{t('Accetta come fornitori')}</button>
              <select value={unisciIn} onChange={(e) => setUnisciIn(e.target.value)} aria-label={t('Unisci a')}>
                <option value="">{t('unisci a…')}</option>
                {(fornitori.data ?? []).map((f) => <option key={f.id} value={f.id}>{f.name}</option>)}
              </select>
              <button type="button" className="secondary" disabled={busy || !unisciIn} onClick={() => void esegui(async () => {
                const r = await api.mergeSupplierCandidates(chiavi, unisciIn)
                return t('{n} nomi uniti', { n: r.merged })
              })}>{t('Unisci')}</button>
              <button type="button" className="secondary" disabled={busy} onClick={() => void esegui(async () => {
                const r = await api.ignoreSupplierCandidates(chiavi)
                return t('{n} nomi ignorati', { n: r.ignored })
              })}>{t('Ignora')}</button>
            </>
          ) : (
            <button type="button" disabled={busy} onClick={() => void esegui(async () => {
              const r = await api.restoreSupplierCandidates(chiavi)
              return t('{n} nomi di nuovo proposti', { n: r.restored })
            })}>{t('Rimetti fra i proposti')}</button>
          )}
        </div>
      )}
      {dati.loading && !dati.data ? <Spinner /> : (dati.data ?? []).length === 0 ? (
        <EmptyState title={stato === 'proposed' ? t('Niente da catalogare') : t('Nessun nome ignorato')}
                    hint={stato === 'proposed' ? t('Ogni nome trovato è già un fornitore o è stato ignorato.') : undefined} />
      ) : (
        <DataTable rows={dati.data ?? []} columns={colonne} rowKey={(c) => c.key} conteggio={{ nome: t('nomi') }}
                   selezione={scrive ? { chiavi: scelti, onCambia: setScelti, id: (c) => c.key } : undefined} />
      )}
    </Card>
  )
}


function TutteLeRichieste({ scrive }: { scrive: boolean }) {
  const dati = useAsync(() => api.allSupplierRequests(), [])
  return (
    <Card>
      <p className="field__hint" style={{ marginTop: 0 }}>
        {t('Le richieste ai fornitori: vulnerabilità dei loro prodotti trovate qui, fine supporto, SBOM, questionari. Si scrivono dalla scheda del fornitore («Chiedi al fornitore»); il sollecito parte da solo prima della scadenza e alla scadenza.')}
      </p>
      <ErrorBanner error={dati.error} />
      {dati.loading && !dati.data ? <Spinner /> : (
        <TabellaRichieste righe={dati.data ?? []} conFornitore scrive={scrive} onCambiato={() => void dati.reload()} />
      )}
    </Card>
  )
}
